import { SectionRole } from "../../shared/types/roles";

import {
  classUserSelectors,
  createAppSelector,
  selectCurrentSectionId,
  selectCurrentUserId,
  userSelectors,
} from "./base";
import {
  selectCurrentBreakoutGroup,
  selectIsInBreakout,
  selectOrderedBreakoutGroups,
} from "./breakouts";
import {
  selectUserIdsWhoCanMentionAllInChat,
  selectUserIdsWhoCanMentionAnyBreakoutGroupInChat,
} from "./permissions";
import { selectEnabledSectionUsersForCurrentSection } from "./section-user";

import type { MentionTarget } from "components/chat/mentions";
import type { UserType } from "components/server-types";
import { filterMap } from "utils/collections";
import { getUserFullName, getUserInitials } from "utils/user-utils";

const selectHumanSectionUsersForCurrentSection = createAppSelector(
  [selectEnabledSectionUsersForCurrentSection],
  (sectionUsers) =>
    sectionUsers.filter((sectionUser) => sectionUser.role !== SectionRole.RECORDING_AGENT),
);

const ALL_MENTION_TARGET: MentionTarget = {
  id: "all",
  kind: "all",
  mentionText: "all",
  searchTerms: ["all"],
};

export type ChatMentionAuthorPermissions = {
  breakoutGroupId: string | null;
  canMentionAll: boolean;
  canMentionAnyBreakoutGroup: boolean;
};

const DEFAULT_CHAT_MENTION_AUTHOR_PERMISSIONS: ChatMentionAuthorPermissions = {
  breakoutGroupId: null,
  canMentionAll: false,
  canMentionAnyBreakoutGroup: false,
};

export const selectChatMentionAuthorPermissionsByUserId = createAppSelector(
  [
    classUserSelectors.filterForCurrentSection,
    selectUserIdsWhoCanMentionAnyBreakoutGroupInChat,
    selectUserIdsWhoCanMentionAllInChat,
  ],
  (classUsers, userIdsWhoCanMentionAnyBreakoutGroup, userIdsWhoCanMentionAll) => {
    const classUserByUserId = new Map(
      classUsers.map((classUser) => [classUser.user_id, classUser]),
    );
    const userIds = new Set([
      ...classUserByUserId.keys(),
      ...userIdsWhoCanMentionAnyBreakoutGroup,
      ...userIdsWhoCanMentionAll,
    ]);

    const mentionAllSet = new Set(userIdsWhoCanMentionAll);
    const mentionAnyBreakoutSet = new Set(userIdsWhoCanMentionAnyBreakoutGroup);

    return new Map(
      [...userIds].map((userId) => [
        userId,
        {
          breakoutGroupId: classUserByUserId.get(userId)?.breakout_group_id ?? null,
          canMentionAll: mentionAllSet.has(userId),
          canMentionAnyBreakoutGroup: mentionAnyBreakoutSet.has(userId),
        },
      ]),
    );
  },
);

export const getChatMentionAuthorPermissions = (
  permissionsByUserId: Map<string, ChatMentionAuthorPermissions>,
  userId: string,
) => permissionsByUserId.get(userId) ?? DEFAULT_CHAT_MENTION_AUTHOR_PERMISSIONS;

export const canAuthorUseChatMentionTarget = (
  target: Pick<MentionTarget, "id" | "kind">,
  permissions: ChatMentionAuthorPermissions,
) => {
  if (target.kind === "user") return true;
  if (target.kind === "all") return permissions.canMentionAll;
  return (
    permissions.canMentionAnyBreakoutGroup ||
    target.id === `breakout:${permissions.breakoutGroupId}`
  );
};

export const filterChatMentionTargetsForAuthor = (
  targets: MentionTarget[],
  permissions: ChatMentionAuthorPermissions,
) => targets.filter((target) => canAuthorUseChatMentionTarget(target, permissions));

const createBreakoutMentionTargets = (
  breakoutGroups: { id: string; number: number }[],
): MentionTarget[] =>
  breakoutGroups.map((group) => {
    const mentionText = `Breakout ${group.number}`;
    const legacyMentionText = `breakout${group.number}`;
    return {
      id: `breakout:${group.id}`,
      kind: "breakout",
      mentionAliases: [legacyMentionText],
      mentionText,
      number: group.number,
      searchTerms: [mentionText, legacyMentionText, String(group.number)],
    };
  });

const createUserMentionTarget = (user: UserType): Extract<MentionTarget, { kind: "user" }> => {
  const displayName = getUserFullName(user);
  return {
    displayName,
    email: user.email,
    id: `user:${user.id}`,
    initials: getUserInitials(user),
    kind: "user",
    mentionText: displayName,
    searchTerms: [displayName, ...displayName.split(/\s+/)],
    userId: user.id,
  };
};

const selectUserMentionTargetsForCurrentSection = createAppSelector(
  [selectHumanSectionUsersForCurrentSection, userSelectors.selectEntities],
  (sectionUsers, users): Extract<MentionTarget, { kind: "user" }>[] =>
    sectionUsers
      .flatMap((sectionUser) => {
        const user = users[sectionUser.user_id];
        if (!user) return [];

        return [createUserMentionTarget(user)];
      })
      .sort((a, b) => a.mentionText.localeCompare(b.mentionText)),
);

export const selectChatMentionTargetsForCurrentUser = createAppSelector(
  [selectCurrentUserId, userSelectors.selectEntities, selectCurrentBreakoutGroup],
  (currentUserId, users, currentBreakoutGroup): MentionTarget[] => {
    const currentUser = currentUserId ? users[currentUserId] : undefined;
    return [
      ALL_MENTION_TARGET,
      ...(currentBreakoutGroup ? createBreakoutMentionTargets([currentBreakoutGroup]) : []),
      ...(currentUser ? [createUserMentionTarget(currentUser)] : []),
    ];
  },
);

export const selectMentionTargetsForCurrentClass = createAppSelector(
  [
    classUserSelectors.filterForCurrentSection,
    selectUserMentionTargetsForCurrentSection,
    selectCurrentUserId,
    selectIsInBreakout,
    selectOrderedBreakoutGroups,
    selectChatMentionAuthorPermissionsByUserId,
  ],
  (
    classUsers,
    userTargets,
    currentUserId,
    isInBreakout,
    breakoutGroups,
    permissionsByUserId,
  ): MentionTarget[] => {
    const presentUserIds = new Set(
      filterMap(
        classUsers,
        (classUser) => classUser.is_in_class_session,
        (classUser) => classUser.user_id,
      ),
    );
    const presentUserTargets = userTargets.filter(
      (target) => presentUserIds.has(target.userId) && target.userId !== currentUserId,
    );

    const currentUserPermissions = currentUserId
      ? getChatMentionAuthorPermissions(permissionsByUserId, currentUserId)
      : DEFAULT_CHAT_MENTION_AUTHOR_PERMISSIONS;
    const visibleBreakoutGroups = !isInBreakout
      ? []
      : currentUserPermissions.canMentionAnyBreakoutGroup
        ? breakoutGroups
        : breakoutGroups.filter((group) => group.id === currentUserPermissions.breakoutGroupId);

    return [
      ...(currentUserPermissions.canMentionAll ? [ALL_MENTION_TARGET] : []),
      ...createBreakoutMentionTargets(visibleBreakoutGroups),
      ...presentUserTargets,
    ];
  },
);

export const selectMentionTargetsForChatHistory = createAppSelector(
  [selectUserMentionTargetsForCurrentSection, selectOrderedBreakoutGroups],
  (userTargets, breakoutGroups): MentionTarget[] => [
    ALL_MENTION_TARGET,
    ...createBreakoutMentionTargets(breakoutGroups),
    ...userTargets,
  ],
);

export const selectUnseenChatMentionMessageIdsForCurrentSection = createAppSelector(
  [selectCurrentSectionId, (state) => state.chatMentions.unseenMessageIdsBySection],
  (sectionId, unseenMessageIdsBySection) =>
    sectionId ? (unseenMessageIdsBySection[sectionId] ?? []) : [],
);

export const selectRequestedChatMentionMessageIdForCurrentSection = createAppSelector(
  [selectCurrentSectionId, (state) => state.chatMentions.requestedMessageIdBySection],
  (sectionId, requestedMessageIdBySection) =>
    sectionId ? (requestedMessageIdBySection[sectionId] ?? null) : null,
);
